import { Domain } from 'library/ts/model/enums/web/Domain';
import { SubscriberPackageKey } from 'library/ts/model/user/SubscriberPackageKey';
import { getSmartTagRuntimeVO } from 'tag/ts/smart_tag/utilities/Runtime';
import { FreewheelConstants } from 'tag/ts/tag/ads/freewheel/constants/FreewheelConstants';
import { FreewheelParameterType } from 'tag/ts/tag/ads/freewheel/enums/FreewheelParameterType';
import { FreewheelRequestType } from 'tag/ts/tag/ads/freewheel/enums/FreewheelRequestType';
import { IFreewheelRequestArgument } from 'tag/ts/tag/ads/freewheel/interfaces/IFreewheelRequestArgument';
import { FreewheelParameter } from 'tag/ts/tag/ads/freewheel/parameters/FreewheelParameter';
import { AbstractFreewheelRequest } from 'tag/ts/tag/ads/freewheel/requests/AbstractFreewheelRequest';
import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { UserModel } from 'tag/ts/tag/model/vo/UserModel';

/**
 * @copyright © Paramount Streaming
 * @author  Zeeshan Ahmad
 * @description Creates and returns Freewheel DAI SSB request
 */
export class FreewheelDaiSsbRequest extends AbstractFreewheelRequest {

	constructor( argument: ITagAbstractClassArgument ) {
		super(
			{
				...argument,
				name: 'FreewheelDaiSsbRequest',
				type: FreewheelRequestType.DAI_SSB
			} as IFreewheelRequestArgument
		);

		const paramsVO = this.tag.params;
		const siteSessionData = this.tag.model.siteUtilities.siteSessionData;
		const apiMetadata = this.tag?.model?.apiMetadata;
		const constants = FreewheelConstants.getInstance();
		const fmsResponse = paramsVO?.AD?.FW?.FMS;
		const gdprResponse = paramsVO?.AD?.FW?.GDPR;
		const runtimeVO = getSmartTagRuntimeVO();
		const isPPlus = !!runtimeVO.top.parsed.host.includes( Domain.PPLUS );
		const coppa = ( !!isPPlus && !this.utils.Type.isEmpty( siteSessionData?.tfcd ) ) ? siteSessionData.tfcd.toString() : undefined;
		const isLive = !!this.tag.model.contentUtilities.isLive( paramsVO, apiMetadata );
		const siteSectionId = siteSessionData?.csid?.toLowerCase() || this.createSiteSectionId();
		const userModel = new UserModel( { tag: this.tag, paramsUser: paramsVO.USER } );
		const currentValidStatus = userModel.getCurrentValidStatus();
		const sb = currentValidStatus?.key === SubscriberPackageKey.SB ? currentValidStatus?.value : undefined;
		const tsb = currentValidStatus?.key === SubscriberPackageKey.TSB ? currentValidStatus?.value : undefined;
		const tfcd = !this.utils.Type.isEmpty( siteSessionData?.tfcd ) ? siteSessionData.tfcd.toString() : undefined;
		const tve = currentValidStatus?.key === SubscriberPackageKey.TVE ? currentValidStatus?.value : undefined;
		const referrer = runtimeVO.top.url.split( '?' )[ 0 ];
		const descUrl = !!isPPlus ? constants.descUrlPPlus : constants.descUrlCBS;
		[
			{ key: 'imafw_csid', value: siteSectionId, type: FreewheelParameterType.GLOBAL },
			{ key: 'imafw__fw_ae', value: paramsVO?.USER?.mvpdId, type: FreewheelParameterType.SITE },
			{ key: 'imafw__fw_continuous_play', value: ( !!isLive ? undefined : siteSessionData?.cpSession ), type: FreewheelParameterType.SITE },
			{ key: 'imafw__fw_coppa', value: coppa, type: FreewheelParameterType.SITE },
			{ key: 'imafw__fw_h_referer', value: referrer, type: FreewheelParameterType.SITE },
			{ key: 'imafw__fw_gdpr', value: gdprResponse?._fw_gdpr?.toString(), type: FreewheelParameterType.SITE },
			{ key: 'imafw__fw_gdpr_consent', value: gdprResponse?._fw_gdpr_consent?.toString(), type: FreewheelParameterType.SITE },
			{ key: 'imafw__fw_us_privacy', value: paramsVO?.USER?.usPrivacy, type: FreewheelParameterType.SITE },
			{ key: 'imafw__fw_vcid2', value: fmsResponse?._fw_vcid2, type: FreewheelParameterType.SITE },
			{ key: 'imafw_region', value: siteSessionData?.region, type: FreewheelParameterType.SITE },
			{ key: 'imafw__fw_is_lat', value: siteSessionData?._fw_is_lat, type: FreewheelParameterType.SITE },
			// NOTE: bundle gets passed into cust_params, and should therefore never have the "imafw_" prefix
			{ key: 'bundle', value: paramsVO?.USER?.bundleStatus, type: FreewheelParameterType.SITE },
			{ key: 'imafw_cpPre', value: ( !!isLive ? undefined : siteSessionData?.cpPre ), type: FreewheelParameterType.CUSTOM },
			{ key: 'imafw_cpSession', value: ( !!isLive ? undefined : siteSessionData?.cpSession ), type: FreewheelParameterType.CUSTOM },
			{ key: 'imafw_description_url', value: descUrl, type: FreewheelParameterType.CUSTOM },
			{ key: 'imafw_ge', value: siteSessionData?.ge, type: FreewheelParameterType.CUSTOM },
			{ key: 'imafw_gr', value: siteSessionData?.gr, type: FreewheelParameterType.CUSTOM },
			{ key: 'imafw_host', value: siteSessionData?.host, type: FreewheelParameterType.CUSTOM },
			{ key: 'imafw_packageSource', value: paramsVO?.USER?.packageSource, type: FreewheelParameterType.CUSTOM },
			{ key: 'imafw_partner', value: siteSessionData?.partner, type: FreewheelParameterType.CUSTOM },
			{ key: 'imafw_ptype', value: siteSessionData?.ptype, type: FreewheelParameterType.CUSTOM },
			{ key: 'imafw_sb', value: sb, type: FreewheelParameterType.CUSTOM },
			{ key: 'imafw_tsb', value: tsb, type: FreewheelParameterType.CUSTOM },
			{ key: 'imafw_section', value: siteSessionData?.section, type: FreewheelParameterType.CUSTOM },
			{ key: 'imafw_session', value: siteSessionData?.session, type: FreewheelParameterType.CUSTOM },
			{ key: 'imafw_sub', value: siteSessionData?.sub, type: FreewheelParameterType.CUSTOM },
			{ key: 'imafw_subses', value: siteSessionData?.subses, type: FreewheelParameterType.CUSTOM },
			{ key: 'imafw_sz', value: constants.size, type: FreewheelParameterType.CUSTOM },
			// NOTE: must pass both tfcd and imafw_tfcd for all DAI ad calls because Google does some modifications and this is how we can pass this value
			{ key: 'tfcd', value: tfcd, type: FreewheelParameterType.CUSTOM },
			{ key: 'imafw_tfcd', value: tfcd, type: FreewheelParameterType.CUSTOM },
			{ key: 'imafw_tve', value: tve, type: FreewheelParameterType.CUSTOM },
			{ key: 'imafw_user', value: paramsVO?.CONTEXT?.metadata?.userProfileCategory, type: FreewheelParameterType.CUSTOM },
			{ key: 'imafw_vguid', value: siteSessionData?.vguid, type: FreewheelParameterType.CUSTOM },
			{ key: 'imafw_source', value: siteSessionData?.source, type: FreewheelParameterType.CUSTOM },
			{ key: 'imafw_showSection', value: siteSessionData?.showSection, type: FreewheelParameterType.CUSTOM },
			{ key: 'imafw_movieSection', value: siteSessionData?.movieSection, type: FreewheelParameterType.CUSTOM },
			{ key: 'imafw_searchEventComplete', value: siteSessionData?.searchEventComplete, type: FreewheelParameterType.CUSTOM },
			{ key: 'imafw__fw_h_x_country', value: siteSessionData?.region, type: FreewheelParameterType.CUSTOM },
			{ key: 'imafw_device_region_language', value: siteSessionData?.device_region_language, type: FreewheelParameterType.CUSTOM },
			{ key: 'imafw_language_selection', value: this.createLanguageSelection(), type: FreewheelParameterType.CUSTOM },
			{ key: 'imafw__fw_content_language', value: siteSessionData?.lang, type: FreewheelParameterType.CUSTOM },
			{ key: 'imafw_endcard', value: siteSessionData?.endcard, type: FreewheelParameterType.CUSTOM },
			{ key: 'imafw_gpp', value: siteSessionData?.gpp, type: FreewheelParameterType.CUSTOM },
			{ key: 'imafw_gpp_sid', value: siteSessionData?.gpp_sid, type: FreewheelParameterType.CUSTOM },
			{ key: 'imafw_vendorUserType', value: siteSessionData?.vendorUserType, type: FreewheelParameterType.CUSTOM },
			{ key: 'imafw_station', value: this.tag?.model?.adUtilities?.getCallSignId?.(), type: FreewheelParameterType.CUSTOM }
		].forEach( ( p ) => this.addParameter( new FreewheelParameter( p.key, p.value, p.type ) ) );

		// create FreewheelParameters from FMS and append them
		this.addFmsParameters( fmsResponse );
	}

	/**
	 * @description Returns request as a qualified ad call url string
	 * @return {string}
	 */
	createAdCallUrl(): string {
		const constants = FreewheelConstants.getInstance();
		const scheme = constants.scheme;
		const host = constants.hostIMA;

		// separate global and other parameters so url is correctly formatted:
		// https://docs.google.com/spreadsheets/d/17j5eNLIwssHBdbss_M7lW4rP5_7lbBMASnyOiKxd7L4/edit#gid=1189587938&range=A2
		const otherParameters = this.getParametersByType( [ FreewheelParameterType.SITE, FreewheelParameterType.CUSTOM ] );
		const globalParamsQueryString = this.getGlobalParameters().map( ( param ) => `${param.key}=${this.toUrlParam( param.value )}` ).join( '&' );
		const otherParametersQueryString = otherParameters.map( ( param ) => `${param.key}=${this.toUrlParam( param.value )}` ).join( '&' );

		const url = `${scheme}://${host}?${globalParamsQueryString};${otherParametersQueryString};`;
		this?.logger?.log( 'createAdCallUrl', 'returning ad call url:', url );

		return url;
	}
}